<script setup lang="ts">
/**
 * Кнопка «добавить» / «в корзине» в плитке каталога.
 *
 * Цвет и скругление берутся из CSS-переменных конфига внешнего вида
 * (--app-accent / --app-accent-incart / --app-radius-control), поэтому в админке
 * достаточно поменять акцент, чтобы кнопка изменилась и в каталоге, и в превью.
 */
import type { CardActionStyle } from '@shared/config';

const props = withDefaults(
  defineProps<{
    /** Товар уже в корзине — меняем иконку, подпись и цвет. */
    inCart?: boolean;
    /** Сколько штук в корзине — показываем счётчиком поверх кнопки. */
    cartQuantity?: number;
    variant?: CardActionStyle;
    /** Растянуть на всю ширину (шаблоны compact и showcase). */
    block?: boolean;
  }>(),
  { inCart: false, cartQuantity: 0, variant: 'icon', block: false },
);

defineEmits<{ (event: 'click', payload: MouseEvent): void }>();

const label = computed(() =>
  props.inCart ? `В корзине${props.cartQuantity > 1 ? ` · ${props.cartQuantity}` : ''}` : 'В корзину',
);
const icon = computed(() =>
  props.inCart ? 'i-heroicons-shopping-cart-solid' : 'i-heroicons-plus-16-solid',
);
const showLabel = computed(() => props.variant !== 'icon');
const showIcon = computed(() => props.variant !== 'text');

/**
 * Счётчик рисуем только у кнопки-иконки: там подписи нет, и без числа непонятно,
 * одна штука в корзине или шесть. В вариантах с текстом количество уже в подписи.
 */
const showBadge = computed(() => props.inCart && props.variant === 'icon' && props.cartQuantity > 0);
</script>

<template>
  <button
    type="button"
    class="pcard-action"
    :class="[
      `pcard-action--${variant}`,
      { 'pcard-action--block': block, 'pcard-action--in-cart': inCart },
    ]"
    :aria-label="label"
    @click.stop="$emit('click', $event)"
  >
    <!--
      Момент «положил в корзину» отмечаем анимированной галочкой: она рисуется
      штрихом ровно тогда, когда гость нажал. `:key` обязателен — анимация
      line-md играет один раз при монтировании, `mode="svg"` тоже: проп-строка
      рендерится CSS-маской, а SVG внутри маски браузер не анимирует.

      Исходное состояние («плюс») остаётся обычной иконкой: в каталоге сотня
      карточек, и сотня одновременно прорисовывающихся плюсов при загрузке —
      это не акцент, а рябь. Плюс к тому вдвое меньше разметки на карточку.
    -->
    <Icon
      v-if="showIcon && inCart"
      key="in-cart"
      mode="svg"
      name="line-md:confirm-circle"
      class="pcard-action__icon"
    />
    <UIcon v-else-if="showIcon" :name="icon" class="pcard-action__icon" />
    <span v-if="showLabel" class="pcard-action__label">{{ label }}</span>
    <span v-if="showBadge" class="pcard-action__badge">{{ cartQuantity }}</span>
  </button>
</template>

<style scoped lang="scss">
.pcard-action {
  @apply relative inline-flex items-center justify-center gap-2 font-semibold shrink-0;
  @apply transition-transform duration-200 active:scale-95;
  border-radius: var(--app-radius-control, 9999px);
  background: var(--app-accent, #ef4444);
  color: var(--app-accent-foreground, #ffffff);
  // Обводка из настройки «Обводка карточек и кнопок»: пока не задана — переменных нет,
  // и кнопка остаётся без рамки, как раньше
  border: var(--app-border-width, 0) solid var(--app-border-color, transparent);
  box-shadow: 0 4px 12px -4px rgb(0 0 0 / 0.35);

  &--in-cart {
    background: var(--app-accent-incart, #16a34a);
    color: var(--app-accent-incart-foreground, #ffffff);
  }

  // Только иконка — круглая (или квадратная, если выбраны квадратные скругления) кнопка
  &--icon {
    @apply w-11 h-11 md:w-12 md:h-12 p-0;
  }

  &--text,
  &--icon-text {
    @apply px-4 py-2.5 text-sm md:text-base;
  }

  &--block {
    @apply w-full;
  }

  &__icon {
    @apply w-5 h-5 md:w-6 md:h-6;
  }

  &__label {
    @apply whitespace-nowrap;
  }

  // Счётчик количества у кнопки-иконки. Контрастная плашка поверх акцента:
  // на фирменном цвете любой яркости число должно читаться.
  &__badge {
    @apply absolute -top-1 -right-1 min-w-[1.25rem] h-5 px-1;
    @apply inline-flex items-center justify-center rounded-full;
    @apply text-xs font-bold leading-none tabular-nums;
    background: #ffffff;
    color: #111111;
    box-shadow: 0 1px 4px rgb(0 0 0 / 0.35);
  }

  &:hover {
    filter: brightness(1.06);
  }
}
</style>
